"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { X, Send, HelpCircle, Trash2, Copy, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { SpeakerButton } from "./speaker-button";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/language-context";
import { useTTS } from "@/lib/use-tts";
import api from "@/lib/api/client";
import { useTranslations } from "@/lib/use-translations";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  feedback?: "up" | "down" | null;
}

interface PhraseChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  phrase: string;
  translation: string;
  nodeId: string;
}

// Store chat history in memory (persists during session)
const chatHistoryStore: Record<string, ChatMessage[]> = {};

// Typing indicator component
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-2">
      <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="w-2 h-2 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

export function PhraseChatModal({ isOpen, onClose, phrase, translation, nodeId }: PhraseChatModalProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => chatHistoryStore[nodeId] || []);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { speak, stop, state: ttsState } = useTTS();

  const handleSpeak = () => {
    if (ttsState === "playing") {
      stop();
    } else {
      speak(phrase);
    }
  };
  const t = useTranslations();

  // Save messages to store when they change
  useEffect(() => {
    chatHistoryStore[nodeId] = messages;
  }, [messages, nodeId]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll to bottom when messages or streaming content changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const copyToClipboard = useCallback(async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const setFeedback = useCallback((index: number, feedback: "up" | "down") => {
    setMessages((prev) => {
      const newMessages = [...prev];
      const msg = newMessages[index];
      if (!msg) return prev;
      const currentFeedback = msg.feedback;
      newMessages[index] = {
        ...msg,
        feedback: currentFeedback === feedback ? null : feedback,
      };
      chatHistoryStore[nodeId] = newMessages;
      return newMessages;
    });
  }, [nodeId]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setStreamingContent("");

    try {
      const { response } = await api.POST("/api/phrase-chat", {
        body: {
          phrase,
          translation,
          targetLanguage: targetLanguage.code,
          nativeLanguage: nativeLanguage.code,
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        },
        parseAs: "stream",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let fullContent = "";
      let buffer = "";

      const processChunk = (chunk: string) => {
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.text) {
                fullContent += parsed.text;
                setStreamingContent(fullContent);
              } else if (parsed.error) {
                throw new Error(parsed.error);
              }
            } catch (e) {
              if (e instanceof SyntaxError) continue; // Skip invalid JSON
              throw e;
            }
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Flush decoder and process remaining buffer
          buffer += decoder.decode();
          if (buffer.trim()) {
            processChunk(buffer);
          }
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        processChunk(buffer);
        buffer = "";
      }

      // Add the complete response to messages
      setMessages([...newMessages, { role: "assistant", content: fullContent }]);
      setStreamingContent("");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to get response";
      setMessages([...newMessages, { role: "assistant", content: `Error: ${errorMessage}` }]);
      setStreamingContent("");
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, phrase, translation, targetLanguage, nativeLanguage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg h-[85dvh] sm:h-[75dvh] bg-background rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col overflow-hidden">
        {/* Header with phrase */}
        <div className="flex-shrink-0 border-b border-border">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              <span className="font-medium">Ask about this phrase</span>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setMessages([]);
                    chatHistoryStore[nodeId] = [];
                  }}
                  className="p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Phrase card */}
          <div className="px-4 pb-4">
            <div className="bg-primary/10 rounded-xl p-3 border border-primary/20">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{phrase}</p>
                  <p className="text-xs text-muted-foreground italic mt-1">{translation}</p>
                </div>
                <SpeakerButton
                  state={ttsState}
                  onClick={handleSpeak}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
          {messages.length === 0 && !isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-muted-foreground p-4">
                <p className="text-sm mb-3">Ask anything about this phrase:</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {[
                    "Break it down word by word",
                    "When would I use this?",
                    "How do I pronounce it?",
                    "Similar expressions?",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        setInput(suggestion);
                        inputRef.current?.focus();
                      }}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "flex",
                    msg.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-4 py-2",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-muted rounded-bl-md"
                    )}
                  >
                    {msg.role === "user" ? (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    ) : (
                      <>
                        <div className="text-sm [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-4 [&>li]:mb-1 [&_code]:bg-black/10 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                        {/* Action buttons */}
                        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/50">
                          <button
                            type="button"
                            onClick={() => copyToClipboard(msg.content, idx)}
                            className="p-1 rounded hover:bg-black/5 transition-colors text-muted-foreground hover:text-foreground"
                            title={t.common.copy}
                          >
                            {copiedIndex === idx ? (
                              <Check className="w-3.5 h-3.5 text-green-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeedback(idx, "up")}
                            className={cn(
                              "p-1 rounded hover:bg-black/5 transition-colors",
                              msg.feedback === "up"
                                ? "text-green-500"
                                : "text-muted-foreground hover:text-foreground"
                            )}
                            title="Good response"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeedback(idx, "down")}
                            className={cn(
                              "p-1 rounded hover:bg-black/5 transition-colors",
                              msg.feedback === "down"
                                ? "text-red-500"
                                : "text-muted-foreground hover:text-foreground"
                            )}
                            title="Poor response"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {/* Streaming response */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] bg-muted rounded-2xl rounded-bl-md px-4 py-2">
                    {streamingContent ? (
                      <div className="text-sm [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-4 [&>li]:mb-1 [&_code]:bg-black/10 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
                        <ReactMarkdown>{streamingContent}</ReactMarkdown>
                      </div>
                    ) : (
                      <TypingIndicator />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              disabled={isLoading}
              className="flex-1 bg-muted rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              className="p-2 rounded-full bg-primary text-primary-foreground disabled:opacity-50 transition-opacity"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
